// Date handling at the request boundary.
//
// The server compares date filters by calendar day in the user's time zone,
// so every datagrid request carries the browser's IANA zone. MUI's date
// picker stores `new Date("YYYY-MM-DD")`, which is UTC midnight of the typed
// day; serialised as an instant it would read as the previous evening in New
// York. For `date` columns the day is sent as written instead, so the server
// never has to guess which day the user meant. `dateTime` pickers denote a
// real instant and are sent as-is.

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const DATE_OPERATORS = new Set([
  "is",
  "not",
  "after",
  "onOrAfter",
  "before",
  "onOrBefore",
]);

/** "YYYY-MM-DD" for a Date or ISO string the date picker produced (UTC midnight of the picked day). */
export function pickedDay(value: unknown): unknown {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? value
      : value.toISOString().slice(0, 10);
  }
  if (typeof value === "string") {
    if (DAY.test(value)) return value;
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
  }
  return value;
}

interface FilterItemLike {
  field?: string;
  operator?: string;
  value?: unknown;
}

function normalizeItem<T extends FilterItemLike>(
  item: T,
  typeOf: (field: string) => string | undefined
): T {
  if (!item?.field || !item.operator || !DATE_OPERATORS.has(item.operator))
    return item;
  if (typeOf(item.field) !== "date") return item;
  const value = pickedDay(item.value);
  return value === item.value ? item : { ...item, value };
}

/**
 * Returns the filter model with every `date`-column value reduced to its day.
 * Accepts MUI's flat `{ items }` model and the advanced `{ groups }` model.
 */
export function normalizeDateFilterValues<M>(
  filterModel: M,
  typeOf: (field: string) => string | undefined
): M {
  if (!filterModel || typeof filterModel !== "object") return filterModel;
  const model = filterModel as unknown as {
    items?: FilterItemLike[];
    groups?: { items?: FilterItemLike[] }[];
  };
  let changed = false;
  const out: Record<string, unknown> = {
    ...(model as Record<string, unknown>),
  };
  if (Array.isArray(model.items)) {
    const items = model.items.map((i) => normalizeItem(i, typeOf));
    if (items.some((i, n) => i !== model.items![n])) {
      out.items = items;
      changed = true;
    }
  }
  if (Array.isArray(model.groups)) {
    const groups = model.groups.map((g) => {
      if (!Array.isArray(g.items)) return g;
      const items = g.items.map((i) => normalizeItem(i, typeOf));
      return items.some((i, n) => i !== g.items![n]) ? { ...g, items } : g;
    });
    if (groups.some((g, n) => g !== model.groups![n])) {
      out.groups = groups;
      changed = true;
    }
  }
  return changed ? (out as unknown as M) : filterModel;
}
